/**
 * Centralized formatting utilities for My Riad Manager
 * All formatting functions should be imported from here to ensure consistency
 * 
 * IMPORTANT: MariaDB returns DECIMAL/FLOAT fields as STRINGS (e.g., "0.00")
 * Always use toNumber() or safeFixed() when dealing with values from the database.
 */

/**
 * Safely convert any value to a number
 * MariaDB returns DECIMAL fields as strings - this handles that case
 * @param {any} value - The value to convert
 * @param {number} defaultValue - Default value if conversion fails (default: 0)
 * @returns {number} The numeric value
 */
export const toNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Safely format a number with fixed decimal places
 * Handles MariaDB DECIMAL strings automatically
 * @param {any} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {any} fallback - Fallback value if conversion fails (default: 0, can be '-' for display)
 * @returns {string} Formatted number string
 */
export const safeFixed = (value, decimals = 2, fallback = 0) => {
  if (value === null || value === undefined || value === '') {
    return typeof fallback === 'string' ? fallback : toNumber(fallback).toFixed(decimals);
  }
  const num = Number(value);
  if (isNaN(num)) {
    return typeof fallback === 'string' ? fallback : toNumber(fallback).toFixed(decimals);
  }
  return num.toFixed(decimals);
};

/**
 * Format currency amount with locale
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: MAD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'MAD') => {
  const numAmount = toNumber(amount);
  return new Intl.NumberFormat('fr-MA', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numAmount) + ' ' + currency;
};

/**
 * Format number with locale (no currency)
 * @param {number} value - The value to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  const numValue = toNumber(value);
  return new Intl.NumberFormat('fr-MA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numValue);
};

/**
 * Format percentage
 * @param {number} value - The percentage value
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value) => {
  return safeFixed(value, 1) + '%';
};

/**
 * Get flux type info from config options
 * @param {Array} fluxTypes - Array of flux type config options
 * @param {string} code - Flux type code
 * @returns {object} Flux info with label, color, or defaults
 */
export const getFluxInfo = (fluxTypes, code) => {
  const flux = fluxTypes?.find(f => f.code === code);
  return {
    code: code,
    label: flux?.label || code,
    color: flux?.color || flux?.metadata?.color || '#6B7280'
  };
};

/**
 * Get channel info from config options
 * @param {Array} channels - Array of channel config options
 * @param {string} code - Channel code
 * @returns {object} Channel info with label, color, commission, etc.
 */
export const getChannelInfo = (channels, code) => {
  const channel = channels?.find(c => c.code === code);
  return {
    code: code,
    label: channel?.label || code,
    color: channel?.color || channel?.metadata?.color || '#6B7280',
    isOta: channel?.metadata?.is_ota || false,
    defaultFlux: channel?.metadata?.default_flux || 'CASH_CAISSE',
    commissionRate: channel?.metadata?.commission_rate || 0
  };
};

/**
 * Get payment method info from config options
 * @param {Array} methods - Array of payment method config options
 * @param {string} code - Method code
 * @returns {object} Method info with label, color, icon, etc.
 */
export const getPaymentMethodInfo = (methods, code) => {
  const method = methods?.find(m => m.code === code);
  return {
    code: code,
    label: method?.label || code,
    color: method?.color || method?.metadata?.color || '#6B7280',
    icon: method?.metadata?.icon || 'credit-card',
    defaultFlux: method?.metadata?.default_flux || 'CASH_CAISSE'
  };
};
